import { BuildingOfficeIcon, HomeIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CustomListItemId,
    CustomListKey,
    formatCalendarDate,
    formatDateNoTime,
    HomeCareLeadId,
    PlacementId,
    RunningFeatures,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import React from "react";
import { Link } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import { useFeature } from "store/selectors/hooks";

export type ClientEngagement = inferProcedureOutput<
    AppRouter["clientEngagements"]["list"]
>[number];

export type EngagementRef =
    | { kind: "placement"; id: PlacementId }
    | { kind: "homeCareLead"; id: HomeCareLeadId };

export type EngagementLink =
    | { type: "placement"; id: PlacementId }
    | { type: "homeCareLead"; id: HomeCareLeadId };

export const engagementPath = (engagement: EngagementLink): string => {
    switch (engagement.type) {
        case "placement":
            return `/clients/show/${engagement.id}`;
        case "homeCareLead":
            return `/home-care-clients/show/${engagement.id}`;
        default: {
            const exhaustiveCheck: never = engagement;
            throw new Error(`Unhandled engagement ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

export const engagementDateLabel = (
    engagement: ClientEngagement,
    today: CalendarDate,
): string => {
    const startedLabel = `Started ${formatDateNoTime(engagement.dateAdded, "")}`;
    switch (engagement.type) {
        case "placement": {
            const moved = CalendarDate.parse(
                engagement.moveTimelineActual ?? "",
            ).getOrElse(null);
            if (!moved) {
                return startedLabel;
            }
            const verb = moved.lessThan(today) ? "Moved" : "Moving";
            return `${verb} ${formatCalendarDate(moved, "")}`;
        }
        case "homeCareLead": {
            const startOfCare = CalendarDate.parse(
                engagement.actualStartDate ?? "",
            ).getOrElse(null);
            if (!startOfCare) {
                return startedLabel;
            }
            const verb = startOfCare.lessThan(today) ? "Care started" : "Care starts";
            return `${verb} ${formatCalendarDate(startOfCare, "")}`;
        }
        default: {
            const exhaustiveCheck: never = engagement;
            throw new Error(`Unhandled engagement ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

interface EngagementsQueryResult {
    engagements: ClientEngagement[] | undefined;
}

const detailVNextFeatureByKind: Record<EngagementRef["kind"], RunningFeatures> = {
    placement: "placementDetailVNext",
    homeCareLead: "homeCareLeadDetailVNext",
};

const useClientEngagements = (current: EngagementRef): EngagementsQueryResult => {
    const detailVNext = useFeature(detailVNextFeatureByKind[current.kind]);
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const options = trpcClient.clientEngagements.list.queryOptions({ ref: current });
    const pathKey = trpcClient.clientEngagements.list.pathKey();
    const query = useQuery({
        ...options,
        enabled: detailVNext,
        placeholderData: () => {
            const cachedLists = queryClient.getQueriesData<ClientEngagement[]>({
                queryKey: pathKey,
            });
            for (const [, cachedList] of cachedLists) {
                if (cachedList?.some(engagement => engagement.id === current.id)) {
                    return cachedList;
                }
            }
            return undefined;
        },
    });
    return { engagements: query.data };
};

const engagementListPath = (kind: EngagementRef["kind"]): string => {
    switch (kind) {
        case "placement":
            return "/clients";
        case "homeCareLead":
            return "/home-care-pipeline";
        default: {
            const exhaustiveCheck: never = kind;
            throw new Error(`Unhandled engagement kind ${String(exhaustiveCheck)}`);
        }
    }
};

export const deleteNavigationPath = (
    current: EngagementRef,
    engagements: ClientEngagement[],
): string => {
    const index = engagements.findIndex(engagement => engagement.id === current.id);
    const target =
        index >= 0 ? (engagements[index - 1] ?? engagements[index + 1]) : engagements[0];
    return target && target.id !== current.id
        ? engagementPath(target)
        : engagementListPath(current.kind);
};

const pruneEngagementFromCaches = (
    queryClient: QueryClient,
    pathKey: readonly unknown[],
    deletedId: PlacementId | HomeCareLeadId,
): void => {
    const cachedLists = queryClient.getQueriesData<ClientEngagement[]>({
        queryKey: pathKey,
    });
    for (const [queryKey, cachedList] of cachedLists) {
        if (cachedList?.some(engagement => engagement.id === deletedId)) {
            queryClient.setQueryData(
                queryKey,
                cachedList.filter(engagement => engagement.id !== deletedId),
            );
        }
    }
};

export const useDeleteNavigation = (current: EngagementRef): (() => string) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const pathKey = trpcClient.clientEngagements.list.pathKey();
    const { engagements } = useClientEngagements(current);
    return () => {
        const path = deleteNavigationPath(current, engagements ?? []);
        pruneEngagementFromCaches(queryClient, pathKey, current.id);
        return path;
    };
};

const tabClasses =
    "flex-shrink-0 min-w-[10.5rem] rounded-lg px-3 py-2 text-left transition";
const currentTabClasses = `${tabClasses} border-2 border-sky-600 bg-sky-50`;
const otherTabClasses = `${tabClasses} border border-gray-200 bg-white hover:border-sky-300`;

interface EngagementTabPresentation {
    TypeIcon: typeof BuildingOfficeIcon;
    typeLabel: string;
    statusName: string;
}

const engagementTabPresentation = (
    engagement: ClientEngagement,
    statusNames: {
        placement: (id: CustomListItemId) => string;
        homeCareLead: (id: CustomListItemId) => string;
    },
): EngagementTabPresentation => {
    switch (engagement.type) {
        case "placement":
            return {
                TypeIcon: BuildingOfficeIcon,
                typeLabel: "Placement",
                statusName: statusNames.placement(engagement.statusListItemId),
            };
        case "homeCareLead":
            return {
                TypeIcon: HomeIcon,
                typeLabel: "Home Care",
                statusName: statusNames.homeCareLead(engagement.statusListItemId),
            };
        default: {
            const exhaustiveCheck: never = engagement;
            throw new Error(`Unhandled engagement ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

interface EngagementTabContentProps {
    engagement: ClientEngagement;
    current: boolean;
}

const EngagementTabContent: React.FC<EngagementTabContentProps> = props => {
    const { engagement, current } = props;
    const { getListItemName: getPlacementStatusName } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { getListItemName: getHomeCareStatusName } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );
    const { TypeIcon, typeLabel, statusName } = engagementTabPresentation(engagement, {
        placement: getPlacementStatusName,
        homeCareLead: getHomeCareStatusName,
    });
    return (
        <>
            <span
                className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide ${
                    current ? "text-sky-800" : "text-gray-500"
                }`}>
                <TypeIcon className="size-3.5 flex-shrink-0" />
                {typeLabel}
            </span>
            <span className="mt-0.5 block text-sm font-semibold text-gray-900 leading-snug">
                {statusName}
            </span>
            <span className="block text-xs text-gray-500">
                {engagementDateLabel(engagement, CalendarDate.todayNoTime)}
            </span>
        </>
    );
};

interface EngagementTabsProps {
    current: EngagementRef;
}

export const EngagementTabs: React.FC<EngagementTabsProps> = props => {
    const { current } = props;
    const { engagements } = useClientEngagements(current);

    if (!engagements || engagements.length < 2) {
        return null;
    }

    return (
        <div className="px-4 pb-3">
            <div className="flex items-stretch gap-2 overflow-x-auto p-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {engagements.map(engagement =>
                    engagement.id === current.id ? (
                        <div
                            key={engagement.id}
                            aria-current="page"
                            className={currentTabClasses}>
                            <EngagementTabContent
                                engagement={engagement}
                                current={true}
                            />
                        </div>
                    ) : (
                        <Link
                            key={engagement.id}
                            to={engagementPath(engagement)}
                            className={otherTabClasses}>
                            <EngagementTabContent
                                engagement={engagement}
                                current={false}
                            />
                        </Link>
                    ),
                )}
            </div>
        </div>
    );
};
